import React, { useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Lock, CheckCircle, Loader2, AlertCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ResetPassword = ({ type = 'admin' }) => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { setError('Passwords do not match'); return; }
    if (password.length < 6) { setError('Password must be at least 6 characters'); return; }
    setError('');
    setLoading(true);
    try {
      const endpoint = type === 'artist' ? '/api/artist/reset-password' : '/api/admin/reset-password';
      const res = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: password })
      });
      const data = await res.json();
      if (res.ok) setSuccess(true);
      else setError(data.detail || 'Failed to reset password');
    } catch { setError('Failed to connect'); } finally { setLoading(false); }
  };

  const loginPath = type === 'artist' ? '/artist/login' : '/admin/login';

  if (!token) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#000', color: '#fff' }}>
        <div style={{ textAlign: 'center', padding: 40 }}>
          <AlertCircle size={40} style={{ color: '#ff4444', marginBottom: 12 }} />
          <h2>Invalid Reset Link</h2>
          <p style={{ color: '#888' }}>This password reset link is invalid or has expired.</p>
          <Link to={loginPath} style={{ color: '#00ffff', textDecoration: 'none' }}>Back to Login</Link>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#000', color: '#fff' }}>
        <div style={{ textAlign: 'center', padding: 40, maxWidth: 400 }}>
          <CheckCircle size={48} style={{ color: '#00c864', marginBottom: 16 }} />
          <h2 style={{ marginBottom: 8 }}>Password Reset!</h2>
          <p style={{ color: '#888', marginBottom: 20 }}>Your password has been updated. You can now log in.</p>
          <Link to={loginPath} style={{ display: 'inline-block', background: '#00ffff', color: '#000', padding: '10px 24px', borderRadius: 6, textDecoration: 'none', fontWeight: 700, fontSize: '0.85rem' }}>
            Go to Login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#000', color: '#fff' }}>
      <div style={{ width: '100%', maxWidth: 400, padding: 40 }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <Lock size={32} style={{ color: '#00ffff', marginBottom: 8 }} />
          <h2 style={{ margin: 0 }}>Reset Password</h2>
          <p style={{ color: '#888', fontSize: '0.85rem' }}>Enter your new password below</p>
        </div>

        <form onSubmit={handleSubmit}>
          {error && <div style={{ background: 'rgba(255,60,60,0.1)', border: '1px solid rgba(255,60,60,0.3)', color: '#ff4444', padding: '8px 12px', borderRadius: 6, fontSize: '0.82rem', marginBottom: 16 }}>{error}</div>}

          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#888', marginBottom: 4 }}>New Password</label>
            <input
              type="password" value={password} onChange={e => setPassword(e.target.value)} required minLength={6}
              style={{ width: '100%', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6, padding: '10px 12px', color: '#fff', fontSize: '0.85rem', boxSizing: 'border-box' }}
              data-testid="new-password"
            />
          </div>

          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#888', marginBottom: 4 }}>Confirm Password</label>
            <input
              type="password" value={confirm} onChange={e => setConfirm(e.target.value)} required minLength={6}
              style={{ width: '100%', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6, padding: '10px 12px', color: '#fff', fontSize: '0.85rem', boxSizing: 'border-box' }}
              data-testid="confirm-password"
            />
          </div>

          <button type="submit" disabled={loading} style={{ width: '100%', background: '#00ffff', color: '#000', padding: '12px', border: 'none', borderRadius: 6, fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer' }} data-testid="submit-reset">
            {loading ? <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> : 'Reset Password'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ResetPassword;
